import React, { useRef, useEffect, useState } from 'react';
import { Modal, Button } from 'antd';
import { FaceDetection } from '@mediapipe/face_detection';
import { Camera } from '@mediapipe/camera_utils';
import '../../index.css';
import { motion } from "framer-motion";
interface WebCamProps {
  onAlertCountReached: (count: number) => void;
  speakingRole: 'assistant' | 'user' | null;
}

const WebCam: React.FC<WebCamProps> = ({ onAlertCountReached}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isStreamActive, setIsStreamActive] = useState(false);
  const [error, setError] = useState('');
  const [camera, setCamera] = useState<Camera | null>(null);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [alertCount, setAlertCount] = useState(0);
  const [okClickCount, setOkClickCount] = useState(0);

  const notificationTimeoutRef = useRef<boolean>(false);

  const [userSpeaks, setUserSpeaks] = useState(false);

  // Add new function to set canvas dimensions
  const updateCanvasDimensions = () => {
    if (videoRef.current && canvasRef.current) {
      const videoWidth = videoRef.current.videoWidth || 640;
      const videoHeight = videoRef.current.videoHeight || 480;

      // Set canvas dimensions to match video
      canvasRef.current.width = videoWidth;
      canvasRef.current.height = videoHeight;
    }
  };

  // Add video metadata loaded handler
  const handleVideoMetadata = () => {
    updateCanvasDimensions();
  };



  useEffect(() => {
    const setupFaceDetection = async () => {
      try {
        const faceDetection = new FaceDetection({
          locateFile: (file) =>
            `https://cdn.jsdelivr.net/npm/@mediapipe/face_detection/${file}`,
        });

        faceDetection.setOptions({
          model: 'short',
          minDetectionConfidence: 0.5,
        });

        faceDetection.onResults((results: any) => {
          const currentFaceCount = results.detections.length;

          if (!notificationTimeoutRef.current) {
            if (currentFaceCount === 0) {
              setModalMessage('No faces detected in the frame.');
              setIsModalVisible(true);
            } else if (currentFaceCount > 1) {
              setModalMessage('Multiple faces detected in the frame.');
              setAlertCount((prev) => prev + 1);
              setIsModalVisible(true);
            }

            notificationTimeoutRef.current = true;
            setTimeout(() => {
              notificationTimeoutRef.current = false;
            }, 1000);
          }

          // Ensure canvas is properly sized before drawing
          updateCanvasDimensions();

          if (canvasRef.current) {
            const ctx = canvasRef.current.getContext('2d');
            if (ctx) {
              const width = canvasRef.current.width;
              const height = canvasRef.current.height;

              // Clear the canvas
              ctx.clearRect(0, 0, width, height);

              if (results.detections && results.detections.length > 0) {
                const faceCount = results.detections.length;
                results.detections.forEach((detection: any) => {
                  const boundingBox = detection?.boundingBox;
                  if (boundingBox) {
                    // Calculate actual pixel coordinates
                    const x = boundingBox.xCenter * width - (boundingBox.width * width) / 2;
                    const y = boundingBox.yCenter * height - (boundingBox.height * height) / 2;
                    const boxWidth = boundingBox.width * width;
                    const boxHeight = boundingBox.height * height;

                    ctx.strokeStyle = faceCount > 1 ? '#FF0000' : '#00FF00';
                    ctx.lineWidth = 2;
                    ctx.strokeRect(x, y, boxWidth, boxHeight);
                  }
                });
              }
            }
          }
        });

        if (videoRef.current) {
          // Add metadata loaded event listener
          videoRef.current.addEventListener('loadedmetadata', handleVideoMetadata);

          const cam = new Camera(videoRef.current, {
            onFrame: async () => {
              if (videoRef.current) {
                await faceDetection.send({ image: videoRef.current });
              }
            },
            width: 1280, //640
            height: 960, //480
          });
          setCamera(cam);
        }

      } catch (err) {
        console.error('Face detection setup error:', err);
        setError('Failed to initialize face detection');
      }
    };

    setupFaceDetection();

    // Cleanup
    return () => {
      stopWebcam();
      if (videoRef.current) {
        videoRef.current.removeEventListener('loadedmetadata', handleVideoMetadata);
      }
    };
  }, []);

  useEffect(() => {
    if (camera) {
      startWebcam();
    }
  }, [camera]);

  const startWebcam = async () => {
    try {
      await camera?.start();
      setIsStreamActive(true);
      setError('');
    } catch (err) {
      setError('Failed to access webcam');
      console.error('Webcam error:', err);
    }
  };

  const stopWebcam = () => {
    camera?.stop();
    setIsStreamActive(false);
  };

  const handleModalOk = () => {
    if (modalMessage === 'Multiple faces detected in the frame.') {
      setOkClickCount((prev) => {
        const newCount = prev + 1;
        if (newCount === 5) {
          onAlertCountReached(alertCount);
        }
        return newCount;
      });
    }
    setIsModalVisible(false);
  };

  //camera line work here 
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [volume, setVolume] = useState(0);
  
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  useEffect(() => {
    const initializeAudio = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            noiseSuppression: true,
            echoCancellation: true,
            autoGainControl: true,
          }
        });
        
        mediaStreamRef.current = stream;

        const audioContext = new AudioContext();
        const analyser = audioContext.createAnalyser();
        const source = audioContext.createMediaStreamSource(stream);

        analyser.fftSize = 256;
        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        source.connect(analyser);

        audioContextRef.current = audioContext;
        analyserRef.current = analyser;
        dataArrayRef.current = dataArray;

        const calculateMicLevel = () => {
          if (analyserRef.current && dataArrayRef.current) {
            analyser.getByteFrequencyData(dataArray);
            const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
            setVolume(average);
            setIsSpeaking(average > 20);
          }
          animationFrameRef.current = requestAnimationFrame(calculateMicLevel);
        };

        calculateMicLevel();
      } catch (err) {
        console.error("Error accessing microphone:", err);
      }
    };

    initializeAudio();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      if (audioContextRef.current && audioContextRef.current.state !== "closed") {
        audioContextRef.current.close().catch((err) => {
          console.error("Error closing AudioContext:", err);
        });
      }

      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }

      analyserRef.current = null;
      dataArrayRef.current = null;
      audioContextRef.current = null;
      mediaStreamRef.current = null;
      setIsSpeaking(false);
    };
  }, []);



  

  return (
    <div className="w-full md:w-[800px] flex  mx-auto p-4 rounded-lg">

      <div className="relative w-full">
        <video
          ref={videoRef}
          className={`w-full videoHeight object-cover  shadow-lg rounded-[16.71px] transition-all duration-300 ${isSpeaking
              ? "border-4 border-[#437AFF] ring-4 ring-[#437AFF] ring-opacity-50"
              : "border-transparent"
            }`}
          autoPlay
          muted
          playsInline
        />
        <div className="flex bg-blue-600 absolute bottom-3 top-3 right-3 rounded-full justify-center items-center w-8 h-8 p-1 z-50">
               {/* Left Dot */}
               <motion.div
                 className="w-1 bg-white rounded-full"
                 animate={{
                   height: isSpeaking ? `${Math.min(volume / 40, 0.8)}rem` : "0.3rem",
                 }}
                 transition={{
                   duration: 0.1,
                 }}
               />
       
               {/* Center Dot */}
               <motion.div
                 className="w-1 bg-white rounded-full mx-1"
                 animate={{
                   height: isSpeaking ? `${Math.min(volume / 25, 1.2)}rem` : "0.3rem",
                 }}
                 transition={{
                   duration: 0.1,
                 }}
               />
       
               {/* Right Dot */}
               <motion.div
                 className="w-1 bg-white rounded-full"
                 animate={{
                   height: isSpeaking ? `${Math.min(volume / 40, 0.8)}rem` : "0.3rem",
                 }}
                 transition={{
                   duration: 0.1,
                 }}
               />
             </div>

      </div>
      {error && <div className="text-red-500 mt-2">{error}</div>}

      <Modal
        title={
          <div className=' text-lg text-center font-bold text-red-600'>
             Face Detection Alert 
          </div>
        }
        
        open={isModalVisible}
        onOk={handleModalOk}
        closable={false}
        footer={[
          <Button
            key="ok"
            type="primary"
            style={{
              backgroundColor: '#FF4D4F',
              borderColor: '#FF4D4F',
              color: '#fff',
            }}
            onClick={handleModalOk}
          
          >
            Acknowledge
          </Button>,
        ]}
        centered
       
      >
        <div className=' flex justify-center items-center'>
        <p  className=' text-lg text-current font-semibold'>
          {modalMessage}
        </p>
        </div>
        
       
      </Modal>
    </div>
  );
};

export default WebCam;